import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, ScatterChart,
  Scatter, Cell,
} from 'recharts';
import {
  MODEL_REGISTRY, DRUGS, FEATURE_IMPORTANCE, PREDICTIONS,
  TRAINING_HISTORY, DATASET_STATS, BENCHMARK,
} from './data/mlData';

// ── DESIGN TOKENS — Data Science / Research Workbench ─────────────────
const T = {
  bg:       '#F8F9FA', bgCard:  '#FFFFFF', bgDark:   '#1A1F2E',
  bgPanel:  '#F0F2F5', bgCode:  '#0D1117',
  navy:     '#0A2540', blue:    '#0057FF', blueLight: '#E8F0FE',
  teal:     '#00897B', tealLight: '#E0F2F1',
  purple:   '#6200EA', purpleLight: '#EDE7F6',
  amber:    '#F57C00', amberLight: '#FFF3E0',
  red:      '#D32F2F', redLight:   '#FFEBEE',
  green:    '#2E7D32', greenLight: '#E8F5E9',
  text:     '#1A1F2E', textMuted: '#5F6B7A', textDim: '#9BA8B5',
  border:   '#E2E8F0', borderDark: '#CBD5E0',
  white:    '#FFFFFF',
  // Drug colors
  INH: '#E53935', RIF: '#FB8C00', PZA: '#FDD835',
  EMB: '#43A047', MFX: '#1E88E5', BDQ: '#8E24AA',
  LZD: '#00ACC1', CFZ: '#E91E63',
};

const SANS = "'Inter', 'Segoe UI', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; }
  body { background: ${T.bg}; color: ${T.text}; font-family: ${SANS}; font-size: 14px; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: ${T.bg}; }
  ::-webkit-scrollbar-thumb { background: ${T.borderDark}; border-radius: 3px; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  .fade-up { animation: fadeUp 0.25s ease forwards; }
`;

// ── HELPERS ───────────────────────────────────────────────────────────
const pct = v => `${(v * 100).toFixed(1)}%`;
const card = (children, style = {}) => (
  <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, ...style }}>
    {children}
  </div>
);
const tag = (label, color, bg) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, color, background: bg || color + '18', border: `1px solid ${color}30` }}>{label}</span>
);
const metricColor = v => v >= 0.97 ? T.green : v >= 0.94 ? T.teal : v >= 0.90 ? T.amber : T.red;

// ── COMPONENTS ────────────────────────────────────────────────────────
function SectionTitle({ icon, title, sub }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: T.navy }}>{title}</h2>
      </div>
      {sub && <p style={{ fontSize: 13, color: T.textMuted, marginTop: 3, marginLeft: 28 }}>{sub}</p>}
    </div>
  );
}

function MetricCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '16px 18px', borderTop: `3px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</span>
        <span style={{ fontSize: 18 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, letterSpacing: '-0.5px' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── MODEL CARD ────────────────────────────────────────────────────────
function ModelCard({ model, onSelect, selected }) {
  const statusCfg = {
    production:   { color: T.green,  bg: T.greenLight,  label: 'Production'   },
    experimental: { color: T.amber,  bg: T.amberLight,  label: 'Experimental' },
    archived:     { color: T.textDim,bg: T.bgPanel,     label: 'Archived'     },
  }[model.status];

  return (
    <div onClick={() => onSelect(model)} style={{
      background: T.bgCard, border: `2px solid ${selected ? T.blue : T.border}`,
      borderRadius: 10, padding: '16px', cursor: 'pointer', transition: 'all 0.15s',
      boxShadow: selected ? `0 0 0 3px ${T.blueLight}` : 'none',
    }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = T.borderDark; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = T.border; }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: T.navy }}>{model.name}</div>
        {tag(statusCfg.label, statusCfg.color, statusCfg.bg)}
      </div>
      <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 12, lineHeight: 1.6 }}>{model.description}</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {tag(model.method, T.blue)}
        {model.kmer_size && tag(`k=${model.kmer_size}`, T.purple)}
        {tag(`${(model.feature_count / 1000).toFixed(1)}K features`, T.teal)}
        {tag(`n=${model.training_samples.toLocaleString()}`, T.textMuted)}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Accuracy</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: metricColor(model.overall_accuracy) }}>{pct(model.overall_accuracy)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.6px' }}>AUC-ROC</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: metricColor(model.overall_auc) }}>{model.overall_auc}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Drugs</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{Object.keys(model.per_drug).length}</div>
        </div>
      </div>
    </div>
  );
}

// ── PER-DRUG PERFORMANCE TABLE ────────────────────────────────────────
function DrugPerformanceTable({ model }) {
  const drugs = DRUGS.filter(d => model.per_drug[d.id]);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: T.bgPanel }}>
            {['Drug', 'Line', 'Accuracy', 'AUC-ROC', 'Sensitivity', 'Specificity', 'F1 Score', 'MCC'].map(h => (
              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: `2px solid ${T.border}`, whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {drugs.map((drug, i) => {
            const m = model.per_drug[drug.id];
            return (
              <tr key={drug.id} style={{ background: i % 2 === 0 ? T.bgCard : T.bg, borderBottom: `1px solid ${T.border}` }}>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: T[drug.id] || T.blue, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>{drug.name}</span>
                    <span style={{ fontFamily: MONO, fontSize: 11, color: T.textDim }}>({drug.id})</span>
                  </div>
                </td>
                <td style={{ padding: '10px 14px' }}>{tag(`Line ${drug.line}`, drug.line === 1 ? T.teal : T.purple)}</td>
                {[m.accuracy, m.auc, m.sensitivity, m.specificity, m.f1, m.mcc].map((val, vi) => (
                  <td key={vi} style={{ padding: '10px 14px', fontFamily: MONO, fontSize: 13, fontWeight: 700, color: metricColor(val) }}>
                    {val >= 0.1 ? pct(val) : val.toFixed(3)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── CONFUSION MATRIX ──────────────────────────────────────────────────
function ConfusionMatrix({ drug, data }) {
  const total = data.tp + data.tn + data.fp + data.fn;
  const cells = [
    { label: 'True Positive',  val: data.tp, color: T.green,  sub: 'Correctly predicted resistant'   },
    { label: 'False Negative', val: data.fn, color: T.red,    sub: 'Missed resistant (dangerous!)'   },
    { label: 'False Positive', val: data.fp, color: T.amber,  sub: 'Over-predicted resistant'         },
    { label: 'True Negative',  val: data.tn, color: T.teal,   sub: 'Correctly predicted susceptible' },
  ];
  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.navy, marginBottom: 12 }}>Confusion Matrix — {DRUGS.find(d => d.id === drug)?.name || drug}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {cells.map(c => (
          <div key={c.label} style={{ padding: '14px', background: c.color + '12', borderRadius: 8, border: `1px solid ${c.color}30` }}>
            <div style={{ fontSize: 10, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: c.color, fontFamily: MONO }}>{c.val.toLocaleString()}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{pct(c.val / total)} · {c.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── FEATURE IMPORTANCE ────────────────────────────────────────────────
function FeatureImportancePanel({ drug }) {
  const features = FEATURE_IMPORTANCE[drug] || FEATURE_IMPORTANCE['INH'];
  const maxImportance = Math.max(...features.map(f => f.importance));
  return (
    <div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>
        Top k-mer features for <strong>{DRUGS.find(d => d.id === drug)?.name || drug}</strong> resistance prediction. Novel signals (not in WHO catalogue) may represent emerging resistance mutations.
      </div>
      {features.map((f, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontFamily: MONO, fontSize: 12, color: T.navy, fontWeight: 600 }}>{f.feature}</span>
              <span style={{ fontSize: 11, color: T.textMuted }}>→ {f.gene} {f.mutation}</span>
              {!f.known && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: T.purpleLight, color: T.purple, border: `1px solid ${T.purple}30`, fontWeight: 600 }}>★ Novel signal</span>}
            </div>
            <span style={{ fontFamily: MONO, fontSize: 12, color: T.textMuted }}>{(f.importance * 100).toFixed(1)}%</span>
          </div>
          <div style={{ height: 8, background: T.bgPanel, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(f.importance / maxImportance) * 100}%`, background: f.known ? T.blue : T.purple, borderRadius: 4, transition: 'width 0.6s ease' }} />
          </div>
        </div>
      ))}
      <div style={{ marginTop: 12, display: 'flex', gap: 12, fontSize: 11, color: T.textMuted }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, background: T.blue, borderRadius: 2, display: 'inline-block' }} />Known WHO resistance mutation</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, background: T.purple, borderRadius: 2, display: 'inline-block' }} />Novel signal — requires validation</span>
      </div>
    </div>
  );
}

// ── PREDICTION RESULT ─────────────────────────────────────────────────
function PredictionResult({ pred }) {
  const resistant  = pred.results.filter(r => r.prediction === 'Resistant');
  const clsColor   = { 'DS-TB': T.green, 'HR-TB': T.amber, 'RR-TB': T.red, 'MDR-TB': T.red, 'Pre-XDR-TB': T.purple, 'XDR-TB': '#C62828' }[pred.classification] || T.textMuted;

  return (
    <div className="fade-up">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: T.navy }}>{pred.sample_code}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
            Model: {pred.model_id} · {new Date(pred.predicted_at).toLocaleString()}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ padding: '4px 12px', borderRadius: 99, fontSize: 13, fontWeight: 700, color: clsColor, background: clsColor + '18', border: `1px solid ${clsColor}30` }}>{pred.classification}</span>
        </div>
      </div>

      {/* Drug predictions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        {pred.results.map(r => {
          const isRes = r.prediction === 'Resistant';
          const color = isRes ? T.red : T.green;
          const discordant = r.concordant === false;
          return (
            <div key={r.drug} style={{ padding: '12px', background: color + '08', border: `1px solid ${color}30`, borderRadius: 8, borderLeft: `4px solid ${color}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div>
                  <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: T.text }}>{r.drug}</div>
                  <div style={{ fontSize: 10, color: T.textMuted }}>{r.drug_name}</div>
                </div>
                {discordant && <span style={{ fontSize: 9, color: T.amber, fontWeight: 600 }}>⚠ DISC</span>}
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color }}>{r.prediction}</div>
              {/* Probability bar */}
              <div style={{ marginTop: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: T.textDim, marginBottom: 2 }}>
                  <span>Confidence</span>
                  <span style={{ fontFamily: MONO }}>{pct(r.prob)}</span>
                </div>
                <div style={{ height: 5, background: T.bgPanel, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${r.prob * 100}%`, background: color, borderRadius: 3 }} />
                </div>
              </div>
              {r.dst_result && (
                <div style={{ fontSize: 10, color: r.concordant ? T.green : T.red, marginTop: 5, fontWeight: 600 }}>
                  DST: {r.dst_result} {r.concordant ? '✓' : '✗ mismatch'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Key mutations & novel signals */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 14px', background: T.redLight, borderRadius: 8, border: `1px solid ${T.red}20` }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.red, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Key Resistance Mutations</div>
          {pred.key_mutations.map((m, i) => (
            <div key={i} style={{ fontFamily: MONO, fontSize: 12, color: T.text, marginBottom: 3 }}>· {m}</div>
          ))}
        </div>
        <div style={{ padding: '12px 14px', background: T.purpleLight, borderRadius: 8, border: `1px solid ${T.purple}20` }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.purple, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>★ Novel Signals Detected</div>
          {pred.novel_signals.map((s, i) => (
            <div key={i} style={{ fontFamily: MONO, fontSize: 12, color: T.text, marginBottom: 3 }}>· {s}</div>
          ))}
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 6 }}>Novel signals require experimental validation. Forwarded to Module 5 for surveillance tracking.</div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────
export default function App() {
  const [user,        setUser]        = useState(null);
  const [loginForm,   setLoginForm]   = useState({ username: '', password: '' });
  const [loginErr,    setLoginErr]    = useState('');
  const [activeTab,   setActiveTab]   = useState('overview');
  const [selectedModel, setSelectedModel] = useState(MODEL_REGISTRY[0]);
  const [selectedDrug,  setSelectedDrug]  = useState('INH');
  const [confDrug,      setConfDrug]      = useState('INH');

  const USERS = {
    'ml.analyst':    { id: 'u1', name: 'ML Analyst',           role: 'analyst',    password: 'ML@2026'      },
    'bio.scientist': { id: 'u2', name: 'Senior Bioinformatician', role: 'senior',   password: 'Bio@2026'     },
    'lab.manager':   { id: 'u3', name: 'Laboratory Manager',    role: 'manager',   password: 'Manager@2026' },
  };

  const doLogin = () => {
    const u = USERS[loginForm.username.trim().toLowerCase()];
    if (!u || u.password !== loginForm.password) { setLoginErr('Invalid credentials'); return; }
    setUser(u); setLoginErr('');
  };

  // Benchmark chart data
  const benchmarkData = Object.entries(BENCHMARK).map(([drug, vals]) => ({
    drug,
    TBProfiler:       Math.round(vals.tbprofiler * 1000) / 10,
    'WHO Catalogue':  Math.round(vals.who_catalogue * 1000) / 10,
    'TBGenSurv (ours)': Math.round(vals.tbgensurv * 1000) / 10,
  }));

  // Per-drug radar data
  const radarData = DRUGS.filter(d => selectedModel.per_drug[d.id]).map(d => ({
    drug: d.id,
    Accuracy:    Math.round(selectedModel.per_drug[d.id].accuracy * 1000) / 10,
    AUC:         Math.round(selectedModel.per_drug[d.id].auc * 1000) / 10,
    Sensitivity: Math.round(selectedModel.per_drug[d.id].sensitivity * 1000) / 10,
    Specificity: Math.round(selectedModel.per_drug[d.id].specificity * 1000) / 10,
  }));

  // Dataset pie
  const datasetPie = DATASET_STATS.sources.map(s => ({ name: s.name, value: s.n }));

  const TABS = [
    { id: 'overview',   label: '📊 Overview'           },
    { id: 'models',     label: '🤖 Model Registry'      },
    { id: 'performance',label: '📈 Performance'         },
    { id: 'features',   label: '🔬 Feature Importance'  },
    { id: 'predictions',label: '⚡ Predictions'          },
    { id: 'dataset',    label: '🗄 Dataset'              },
    { id: 'benchmark',  label: '🏆 Benchmark'           },
  ];

  // ── LOGIN ──
  if (!user) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, #0A2540 0%, #1A3A5C 50%, #0A2540 100%)` }}>
      <style>{css}</style>
      <div style={{ width: 420, animation: 'fadeUp 0.35s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
          <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: T.white, letterSpacing: -0.5 }}>TBGenSurv</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>Module 4 · ML Resistance Prediction Engine</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>k-mer · XGBoost · Random Forest · Neural Network</div>
        </div>
        <div style={{ background: T.bgCard, borderRadius: 12, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
          {loginErr && <div style={{ marginBottom: 14, padding: '8px 12px', background: T.redLight, border: `1px solid ${T.red}30`, borderRadius: 6, fontSize: 12, color: T.red }}>⚠ {loginErr}</div>}
          {['username', 'password'].map(f => (
            <div key={f} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{f}</div>
              <input type={f === 'password' ? 'password' : 'text'} value={loginForm[f]} onChange={e => setLoginForm(p => ({ ...p, [f]: e.target.value }))}
                placeholder={f === 'username' ? 'ml.analyst' : '••••••••'}
                onKeyDown={e => e.key === 'Enter' && doLogin()}
                style={{ width: '100%', padding: '10px 14px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 14, outline: 'none' }} />
            </div>
          ))}
          <button onClick={doLogin} style={{ width: '100%', padding: 12, background: T.blue, border: 'none', borderRadius: 8, color: T.white, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Sign In →</button>
          <details style={{ marginTop: 14 }}>
            <summary style={{ fontSize: 12, color: T.textDim, cursor: 'pointer' }}>Demo credentials ▾</summary>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(USERS).map(([un, u]) => (
                <div key={un} onClick={() => setLoginForm({ username: un, password: u.password })}
                  style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 10px', background: T.bg, borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                  <span style={{ fontFamily: MONO, color: T.blue }}>{un}</span>
                  <span style={{ color: T.textDim }}>{u.role}</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: T.bg }}>
      <style>{css}</style>

      {/* Top bar */}
      <div style={{ background: T.navy, padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: T.white }}>TBGenSurv</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', padding: '2px 10px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 99 }}>Module 4 · ML Engine</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{user.name}</span>
          <button onClick={() => setUser(null)} style={{ padding: '5px 12px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 12 }}>Sign out</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: T.bgCard, borderBottom: `1px solid ${T.border}`, padding: '0 28px', display: 'flex', gap: 0, overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '12px 16px', border: 'none', borderBottom: `2px solid ${activeTab === t.id ? T.blue : 'transparent'}`, background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: activeTab === t.id ? T.blue : T.textMuted, whiteSpace: 'nowrap', transition: 'color 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '28px', maxWidth: 1500, margin: '0 auto', width: '100%' }}>

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div className="fade-up">
            <SectionTitle icon="📊" title="ML Engine Overview" sub="TBGenSurv k-mer resistance prediction — extended from HIV-1 NRTI pipeline (Mhlanga 2025)" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
              <MetricCard label="Production Model"  value="97.4%" sub="Overall accuracy — XGBoost v1.0" color={T.green}  icon="🤖" />
              <MetricCard label="AUC-ROC"            value="0.991"  sub="Area under ROC curve"            color={T.teal}   icon="📈" />
              <MetricCard label="Training Isolates"  value="12,847" sub="CRyPTIC + PATRIC datasets"       color={T.blue}   icon="🧬" />
              <MetricCard label="Drugs Covered"      value="8"      sub="First + second line TB drugs"    color={T.purple} icon="💊" />
            </div>

            {/* Pipeline connection */}
            <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px 24px', marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 16 }}>🔗 Pipeline Position</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto' }}>
                {[
                  { label: 'Module 1', sub: 'Sample Tracking', color: T.teal,   done: true  },
                  { label: 'Module 2', sub: 'NMRL Lab',         color: T.blue,   done: true  },
                  { label: 'Module 3', sub: 'WGS Pipeline',     color: T.purple, done: true  },
                  { label: 'Module 4', sub: 'ML Prediction',    color: T.red,    done: false, current: true },
                  { label: 'Module 5', sub: 'Geospatial',       color: T.amber,  done: false },
                  { label: 'Module 6', sub: 'Dashboard',        color: T.textDim,done: false },
                ].map((m, i, arr) => (
                  <React.Fragment key={m.label}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: m.current ? m.color : m.done ? m.color + '20' : T.bgPanel, border: `2px solid ${m.done || m.current ? m.color : T.border}`, fontSize: 14, fontWeight: 700, color: m.current ? T.white : m.done ? m.color : T.textDim, boxShadow: m.current ? `0 0 16px ${m.color}40` : 'none' }}>
                        {m.done && !m.current ? '✓' : i + 1}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: m.current ? 700 : 500, color: m.current ? m.color : m.done ? T.text : T.textDim, textAlign: 'center', marginTop: 6 }}>{m.label}</div>
                      <div style={{ fontSize: 10, color: T.textDim, textAlign: 'center' }}>{m.sub}</div>
                    </div>
                    {i < arr.length - 1 && <div style={{ flex: 1, height: 2, background: m.done ? m.color + '40' : T.border, marginBottom: 22, minWidth: 20 }} />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* HIV → TB transfer learning note */}
            <div style={{ background: T.blueLight, border: `1px solid ${T.blue}30`, borderRadius: 10, padding: '16px 20px', marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.blue, marginBottom: 8 }}>🧬 Methodology: Transfer from HIV Drug Resistance Project</div>
              <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.7 }}>
                This module directly extends the HIV-1 NRTI resistance prediction pipeline (Mhlanga 2025). The same k-mer feature extraction approach — breaking amino acid sequences into overlapping k-mers of length k — is applied to M. tuberculosis protein sequences. Features are extracted from genes known to harbour resistance mutations (rpoB, katG, inhA, embB, gyrA, pncA etc.) and combined with SNP-derived features from the GATK variant calling output (Module 3). XGBoost was selected over Random Forest after benchmarking showed consistently higher performance, particularly for PZA resistance which has heterogeneous mutation patterns.
              </p>
            </div>

            {/* Training curve */}
            <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px 24px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 16 }}>Training Convergence — XGBoost v1.0</div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={TRAINING_HISTORY} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="epoch" tick={{ fontSize: 11, fill: T.textMuted }} label={{ value: 'Epoch', position: 'insideBottom', offset: -2, fontSize: 11, fill: T.textMuted }} />
                  <YAxis tick={{ fontSize: 11, fill: T.textMuted }} domain={[0.6, 1.0]} />
                  <Tooltip contentStyle={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12 }} formatter={v => pct(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="accuracy"     stroke={T.blue}   strokeWidth={2} dot={false} name="Train Accuracy" />
                  <Line type="monotone" dataKey="val_accuracy" stroke={T.green}  strokeWidth={2} dot={false} name="Val Accuracy" strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="loss"         stroke={T.red}    strokeWidth={2} dot={false} name="Train Loss" />
                  <Line type="monotone" dataKey="val_loss"     stroke={T.amber}  strokeWidth={2} dot={false} name="Val Loss" strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* ── MODEL REGISTRY ── */}
        {activeTab === 'models' && (
          <div className="fade-up">
            <SectionTitle icon="🤖" title="Model Registry" sub="Version-controlled ML models. Production model used for all clinical predictions." />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              {MODEL_REGISTRY.map(m => <ModelCard key={m.id} model={m} onSelect={setSelectedModel} selected={selectedModel?.id === m.id} />)}
            </div>
            {selectedModel && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 16 }}>
                  {selectedModel.name} — Per-Drug Performance
                </div>
                {card(<DrugPerformanceTable model={selectedModel} />, { overflow: 'hidden' })}
              </div>
            )}
          </div>
        )}

        {/* ── PERFORMANCE ── */}
        {activeTab === 'performance' && (
          <div className="fade-up">
            <SectionTitle icon="📈" title="Model Performance" sub="Confusion matrices and per-drug metrics for the production model" />
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              {DRUGS.filter(d => selectedModel?.per_drug[d.id]).map(d => (
                <button key={d.id} onClick={() => setConfDrug(d.id)} style={{ padding: '6px 14px', borderRadius: 99, border: `2px solid ${confDrug === d.id ? T[d.id] || T.blue : T.border}`, background: confDrug === d.id ? (T[d.id] || T.blue) + '15' : T.bgCard, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: confDrug === d.id ? (T[d.id] || T.blue) : T.textMuted, transition: 'all 0.15s' }}>
                  {d.id} — {d.name}
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                <ConfusionMatrix drug={confDrug} data={selectedModel?.confusion[confDrug] || { tp: 0, tn: 0, fp: 0, fn: 0 }} />
              </div>
              <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.navy, marginBottom: 12 }}>All Drugs — Accuracy Comparison</div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={radarData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <XAxis type="number" domain={[85, 100]} tick={{ fontSize: 10, fill: T.textMuted }} tickFormatter={v => `${v}%`} />
                    <YAxis type="category" dataKey="drug" tick={{ fontSize: 12, fill: T.textMuted, fontFamily: MONO }} width={35} />
                    <Tooltip contentStyle={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12 }} formatter={v => `${v}%`} />
                    <Bar dataKey="Accuracy" radius={[0, 4, 4, 0]}>
                      {radarData.map((entry, i) => <Cell key={i} fill={T[entry.drug] || T.blue} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* ── FEATURE IMPORTANCE ── */}
        {activeTab === 'features' && (
          <div className="fade-up">
            <SectionTitle icon="🔬" title="Feature Importance & Novel Signal Detection" sub="Top k-mer features driving resistance predictions. Novel signals may represent undiscovered resistance mutations." />
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              {['INH', 'RIF'].map(d => (
                <button key={d} onClick={() => setSelectedDrug(d)} style={{ padding: '6px 14px', borderRadius: 99, border: `2px solid ${selectedDrug === d ? T[d] : T.border}`, background: selectedDrug === d ? T[d] + '15' : T.bgCard, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: selectedDrug === d ? T[d] : T.textMuted }}>
                  {DRUGS.find(dr => dr.id === d)?.name}
                </button>
              ))}
              <span style={{ fontSize: 12, color: T.textDim, alignSelf: 'center', marginLeft: 8 }}>More drugs added as NMRL Zimbabwe data is incorporated into training</span>
            </div>
            {card(<div style={{ padding: '20px' }}><FeatureImportancePanel drug={selectedDrug} /></div>)}
          </div>
        )}

        {/* ── PREDICTIONS ── */}
        {activeTab === 'predictions' && (
          <div className="fade-up">
            <SectionTitle icon="⚡" title="Prediction Results" sub="Drug resistance predictions from WGS inputs. Results shown alongside DST validation where available." />
            {PREDICTIONS.map(pred => (
              <div key={pred.id} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px 24px', marginBottom: 20 }}>
                <PredictionResult pred={pred} />
              </div>
            ))}
          </div>
        )}

        {/* ── DATASET ── */}
        {activeTab === 'dataset' && (
          <div className="fade-up">
            <SectionTitle icon="🗄" title="Training Dataset" sub="Dataset composition, class balance, and geographic distribution" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
              <MetricCard label="Total Isolates"  value={DATASET_STATS.total_isolates.toLocaleString()} color={T.blue}   icon="🧬" />
              <MetricCard label="MDR-TB isolates" value={DATASET_STATS.mdr_tb.toLocaleString()}         color={T.red}    icon="⚠" sub="20.2% of dataset" />
              <MetricCard label="Pre/XDR-TB"      value={(DATASET_STATS.pre_xdr + DATASET_STATS.xdr_tb).toLocaleString()} color={T.purple} icon="!!" sub="6.3% of dataset" />
              <MetricCard label="DS-TB (control)" value={DATASET_STATS.ds_tb.toLocaleString()}          color={T.green}  icon="✓" sub="73.5% of dataset" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 12 }}>Data Sources</div>
                {DATASET_STATS.sources.map((s, i) => (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</span>
                      <span style={{ fontFamily: MONO, fontSize: 12, color: T.textMuted }}>{s.n.toLocaleString()} ({s.pct}%)</span>
                    </div>
                    <div style={{ height: 8, background: T.bgPanel, borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${s.pct}%`, background: s.pct === 0 ? T.border : T.blue, borderRadius: 4, opacity: s.pct === 0 ? 0.3 : 1 }} />
                    </div>
                    {s.pct === 0 && <div style={{ fontSize: 10, color: T.amber, marginTop: 3 }}>⏳ NMRL Zimbabwe data pending — will significantly improve local accuracy</div>}
                  </div>
                ))}
              </div>
              <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 12 }}>Lineage Distribution</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={DATASET_STATS.lineages} layout="vertical" margin={{ left: 0, right: 20 }}>
                    <XAxis type="number" tick={{ fontSize: 10, fill: T.textMuted }} tickFormatter={v => `${v}%`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: T.textMuted }} width={110} />
                    <Tooltip contentStyle={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="pct" fill={T.blue} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ fontSize: 11, color: T.textDim, marginTop: 8 }}>Zimbabwe isolates expected to be predominantly L4 (Euro-American) and L2 (Beijing). NMRL data will validate local lineage distribution.</div>
              </div>
            </div>
          </div>
        )}

        {/* ── BENCHMARK ── */}
        {activeTab === 'benchmark' && (
          <div className="fade-up">
            <SectionTitle icon="🏆" title="Benchmark vs Existing Tools" sub="TBGenSurv vs TBProfiler and WHO 2021 Mutation Catalogue — accuracy by drug" />
            <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px 24px', marginBottom: 20 }}>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={benchmarkData} margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="drug" tick={{ fontSize: 12, fill: T.textMuted }} />
                  <YAxis domain={[80, 100]} tick={{ fontSize: 11, fill: T.textMuted }} tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12 }} formatter={v => `${v}%`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="TBProfiler"        fill={T.textDim} radius={[3,3,0,0]} />
                  <Bar dataKey="WHO Catalogue"     fill={T.blue}    radius={[3,3,0,0]} />
                  <Bar dataKey="TBGenSurv (ours)"  fill={T.green}   radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ background: T.greenLight, border: `1px solid ${T.green}30`, borderRadius: 10, padding: '16px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.green, marginBottom: 8 }}>📝 Key Finding for Publication</div>
              <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.7 }}>
                TBGenSurv outperforms TBProfiler on all first and second-line drugs tested, with the largest improvement seen for PZA (+5.3%) where k-mer features capture heterogeneous pncA mutation contexts that SNP-only approaches miss. Improvement is most pronounced for drugs with complex multi-gene resistance mechanisms (INH: katG + inhA + ahpC). When NMRL Zimbabwe isolates are incorporated into training, local accuracy is expected to improve further as the model learns Zimbabwe-specific resistance mutation patterns.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 28px', background: T.bgCard, borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.textDim }}>
        <span>TBGenSurv Module 4 · ML Resistance Prediction Engine · University of Zimbabwe BIBE</span>
        <span>k-mer extraction · XGBoost · CRyPTIC · WHO 2021 Catalogue · Extended from HIV-1 NRTI pipeline (Mhlanga 2025)</span>
      </div>
    </div>
  );
}
